'use client';
import { useState, useEffect } from 'react';
import ColorThief from 'colorthief';

type RGBColor = [number, number, number];

interface DarkColorResult {
  primaryDark: string;
  secondaryDark: string;
  isLoading: boolean;
}

function isDarkColor(r: number, g: number, b: number): boolean {
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

function rgbToString(color: RGBColor): string {
  return `rgb(${color[0]},${color[1]},${color[2]})`;
}

function calculateLuminance(color: RGBColor): number {
  return 0.299 * color[0] + 0.587 * color[1] + 0.114 * color[2];
}

const defaultPrimaryDark: RGBColor = [18, 18, 18]; // Darker gray
const defaultSecondaryDark: RGBColor = [38, 38, 38]; // Lighter gray

export function useDarkColor(imageUrl: string | null | undefined): DarkColorResult {
  const [primaryDark, setPrimaryDark] = useState<string>(rgbToString(defaultPrimaryDark));
  const [secondaryDark, setSecondaryDark] = useState<string>(rgbToString(defaultSecondaryDark));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!imageUrl) {
      setIsLoading(false);
      return;
    }

    const colorThief = new ColorThief();
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    img.onload = () => {
      try {
        const palette = colorThief.getPalette(img, 8); // Get more colors to have better chances of finding dark ones
        const validPalette = palette.filter(
          (color: RGBColor): color is RGBColor =>
            Array.isArray(color) && color.length === 3 && color.every(value => typeof value === 'number')
        );

        // Get all dark colors and sort them by luminance
        const darkColors = validPalette
          .filter((color: RGBColor) => isDarkColor(color[0], color[1], color[2]))
          .sort((a: RGBColor, b: RGBColor) => calculateLuminance(a) - calculateLuminance(b));

        // If we have at least two dark colors, use them
        if (darkColors.length >= 2) {
          setPrimaryDark(rgbToString(darkColors[0]));
          setSecondaryDark(rgbToString(darkColors[1]));
        }
        // If we only have one dark color, create a slightly lighter version for secondary
        else if (darkColors.length === 1) {
          setPrimaryDark(rgbToString(darkColors[0]));
          const lighterVersion: RGBColor = [
            Math.min(255, darkColors[0][0] + 20),
            Math.min(255, darkColors[0][1] + 20),
            Math.min(255, darkColors[0][2] + 20)
          ];
          setSecondaryDark(rgbToString(lighterVersion));
        }
        // Fallback to defaults if no dark colors found
        else {
          setPrimaryDark(rgbToString(defaultPrimaryDark));
          setSecondaryDark(rgbToString(defaultSecondaryDark));
        }
      } catch (error) {
        console.error('Error extracting dark colors:', error);
        setPrimaryDark(rgbToString(defaultPrimaryDark));
        setSecondaryDark(rgbToString(defaultSecondaryDark));
      } finally {
        setIsLoading(false);
      }
    };

    img.onerror = () => {
      console.error('Error loading image');
      setPrimaryDark(rgbToString(defaultPrimaryDark));
      setSecondaryDark(rgbToString(defaultSecondaryDark));
      setIsLoading(false);
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  return { primaryDark, secondaryDark, isLoading };
}
